<template>
  <div>
    <validation-observer ref="emailForm" tag="form" class="email-form">
      <base-v-input v-model="email"
        :label="$t('form.emailAddress')"
                    rules="required|email"/>
      <checkout-payment-terms v-model="termsAgree" is-tourist-flow/>
      <template v-if="isFromQrCode">
        <base-v-input v-model="sim"
          :label="$t('form.sim')"
                      rules="required|phoneNumberWithSpaces|max:15" />
      </template>
    </validation-observer>
    <bank-form :form-data="paymentForm" ref="bankForm"/>
    <div class="email-form-note">
      <p>{{ $t('checkout.eSIMTouristNotes.part1') }}</p>
      <br/>
      <template v-if="!isFromQrCode">
        <p>{{ $t('checkout.eSIMTouristNotes.part2') }}</p>
        <p>{{ $t('checkout.eSIMTouristNotes.part3') }}</p>
        <br/>
      </template>
      <p v-if="isFreeESimSrb">{{ $t('checkout.eSIMTouristNotes.part5Srb') }}</p>
      <p v-else>{{ $t('checkout.eSIMTouristNotes.part5') }}</p>
    </div>
  </div>
</template>

<script>
import BaseVInput from '@/components/base/base-v-input'
import CheckoutPaymentTerms from '../checkout-payment/checkout-payment-terms.vue'
import { customEvent } from '@/services/customEventBus'
import { paymentOrder, placeOrder, getBlinkingURL } from '@/api/checkout'
import { getCookie, deleteCookie } from '@/utils/cookieController'
import { prepaidTourist, defaultLanguage, englishLanguage } from '@/config/constants'
import { lsSave, lsRemove } from '@/utils/localStorageController'
import BankForm from '../bank-form.vue'

export default {
  name: 'checkout-email-form',
  components: {
    BankForm,
    CheckoutPaymentTerms,
    BaseVInput
  },
  props: {
    lang: {
      type: String,
      default: defaultLanguage
    },
    isFreeESimSrb: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      email: '',
      sim: '',
      termsAgree: false,
      paymentForm: {}
    }
  },
  computed: {
    isFromQrCode () {
      return prepaidTourist.isFromQrCode
    }
  },
  methods: {
    async validateForm () {
      const valid = await this.$refs.emailForm.validate()
      if (valid) this.placeOrder()
    },
    placeOrder () {
      let turnOffLoader = true
      this.$loader.show()
      const dataToSend = {
        cartId: getCookie('guid'),
        email: this.email,
        lang: this.lang,
        paymentMode: 'BY_CARD'
      }

      if (this.isFromQrCode) {
        dataToSend.msisdn = this.sim
      }

      placeOrder(dataToSend).then(response => {
        this.$loader.show()
        if (response.status >= 200 && response.status < 400) {
          lsSave('ec_order_data', JSON.stringify(response.data))
          deleteCookie('guid')
          if (response.data?.paymentMode?.code === 'freeofcharge') {
            turnOffLoader = false
            getBlinkingURL({
              securityGuid: response.data.securityGuid || ''
            }).then(({ data }) => {
              if (data && data.url) {
                window.location.href = data.url
              }
            }).catch(() => {
              this.toast('error', {
                message: this.$t('errors.defaultError')
              })
            })
          } else {
            this.payOrder(response.data.code)
          }
        }
      }).catch(error => {
        let errorMessage = error?.response?.data?.errors[0]?.message
        if (!errorMessage && error?.response?.data?.errors[0]?.subject === 'msisdn') {
          errorMessage = this.$t('errors.rechargeNumberError')
        }
        this.toast('error', {
          message: errorMessage
        })
      }).finally(() => {
        if (turnOffLoader) {
          this.$loader.hide()
        }
      })
    },
    payOrder (code) {
      paymentOrder(code).then(({ data }) => {
        this.paymentForm = data || {}
        if (this.lang === englishLanguage) {
          const bankUrlKeys = ['ReturnURL', 'ReturnErrorURL', 'CancelURL']
          this.paymentForm.parameters.forEach((param) => {
            if (bankUrlKeys.includes(param.key)) { param.value = param.value + '&lang=en' }
          })
        }
        lsSave('ec_show_order_confirmation', true)
        lsSave('ec_order_code', code)
        lsRemove('paymentType')
        setTimeout(() => {
          this.$refs.bankForm.submitForm()
        }, 50)
      }).catch(e => {
        this.handleErrorMessage(e.response.data)
      })
    }
  },
  mounted () {
    customEvent.$on('validate-tourist-step', () => this.validateForm())
  },
  destroyed () {
    customEvent.$off('validate-tourist-step')
  }
}
</script>

<style scoped lang="scss">
.email-form {
  display: flex;
  flex-wrap: wrap;
  background-color: white;
  flex-direction: column;
  padding: 1.6rem;

  @media (map_get($breakpoints, sm)) {
    flex-direction: row;
    align-items: center;
    padding-left: 2.4rem;
  }

  .validated-input {
    padding-bottom: 2rem;

    @media (map_get($breakpoints, sm)) {
      margin-right: 6.4rem;
      width: 31.5rem;
    }
  }

  .checkout-terms {
    padding-top: 0;

    @media (map_get($breakpoints, sm)) {
      margin-top: 3rem;
    }
  }

  &-note {
    margin-top: 2rem;
    margin-bottom: 2rem;

    p {
      margin-bottom: 0;
    }
  }
}
</style>
